/**
 * Schema.org JSON-LD builders for tool, calculator, and comparison pages.
 * Keeps schema shape consistent and reduces boilerplate across pages.
 */

import { SITE_URL } from "@/lib/constants";

const BASE_URL = SITE_URL;
const ORG = { "@type": "Organization" as const, name: "BodyHackGuide", url: BASE_URL };

type BreadcrumbItem = { name: string; href: string };

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.href.startsWith("http") ? it.href : `${BASE_URL}${it.href}`,
    })),
  };
}

type SoftwareArgs = {
  name: string;
  description: string;
  url: string;
  category?: string;
  keywords?: string[];
  /** Optional REAL aggregate rating. Only pass when genuine review data exists —
      never fabricate ratings (Google penalizes invented review data). */
  rating?: { ratingValue: number; ratingCount: number };
};

export function buildSoftwareApplicationSchema({
  name,
  description,
  url,
  category = "HealthApplication",
  keywords,
  rating,
}: SoftwareArgs) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    url: url.startsWith("http") ? url : `${BASE_URL}${url}`,
    applicationCategory: category,
    operatingSystem: "Any (web-based)",
    browserRequirements: "Requires JavaScript",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    creator: ORG,
    ...(keywords ? { keywords: keywords.join(", ") } : {}),
    ...(rating
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: rating.ratingValue,
            ratingCount: rating.ratingCount,
          },
        }
      : {}),
  };
}

type FAQItem = { q: string; a: string };

export function buildFAQSchema(items: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(it => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

type HowToStep = { name: string; text: string };
type HowToArgs = { name: string; description: string; totalTime?: string; steps: HowToStep[]; tools?: string[] };

export function buildHowToSchema({ name, description, totalTime, steps, tools }: HowToArgs) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    // Google flags HowTo without an image; point at the site OG image (resolves).
    image: `${BASE_URL}/og-image.png`,
    ...(totalTime ? { totalTime } : {}),
    ...(tools ? { tool: tools.map(t => ({ "@type": "HowToTool", name: t })) } : {}),
    // schema.org HowToStep has NO "position" property — order is implied by array
    // order. Emitting it triggered a schema.org validation error on every HowTo
    // page (audit 2026-06-29, issue c22e9647).
    step: steps.map(s => ({ "@type": "HowToStep", name: s.name, text: s.text })),
  };
}

type CollectionArgs = {
  name: string;
  description: string;
  url: string;
  numberOfItems?: number;
};

export function buildCollectionPageSchema({ name, description, url, numberOfItems }: CollectionArgs) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: url.startsWith("http") ? url : `${BASE_URL}${url}`,
    ...(numberOfItems ? { numberOfItems } : {}),
    publisher: ORG,
  };
}

type WebPageArgs = {
  name: string;
  description: string;
  url: string;
  dateModified?: string;
};

export function buildWebPageSchema({ name, description, url, dateModified }: WebPageArgs) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: url.startsWith("http") ? url : `${BASE_URL}${url}`,
    ...(dateModified ? { dateModified } : {}),
    publisher: ORG,
  };
}
